import { useEffect, useState, useRef } from 'react';
import { 
  Upload, Trash2, FileText, File, Image, FileSpreadsheet, 
  Loader2, Search, FolderOpen, Edit2, Save, X, Download
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'contracts', label: 'Contracts' },
  { value: 'terms', label: 'Terms & Conditions' },
  { value: 'info', label: 'Property Information' },
  { value: 'activities', label: 'Things To Do' },
  { value: 'maps', label: 'Maps & Directions' },
  { value: 'general', label: 'General' },
];

const getFileIcon = (contentType, filename) => {
  if (contentType?.includes('image')) return <Image className="w-8 h-8 text-blue-500" />;
  if (contentType?.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
  if (contentType?.includes('spreadsheet') || filename?.endsWith('.xlsx') || filename?.endsWith('.csv')) {
    return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
  }
  if (contentType?.includes('word') || filename?.endsWith('.docx') || filename?.endsWith('.doc')) {
    return <FileText className="w-8 h-8 text-blue-600" />;
  }
  return <File className="w-8 h-8 text-gray-500" />;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function FileLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: 'general',
    file: null,
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/file-library`);
      setFiles(res.data);
    } catch (error) {
      toast.error('Failed to load file library');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm(prev => ({
        ...prev,
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, ''), // Use filename without extension as default name
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }
    if (!uploadForm.name.trim()) {
      toast.error('Please enter a name for the file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('name', uploadForm.name);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);

      await axios.post(`${API}/file-library/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('File uploaded successfully');
      setUploadDialogOpen(false);
      setUploadForm({ name: '', description: '', category: 'general', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchFiles();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateFile = async () => {
    if (!editingFile) return;

    try {
      const params = new URLSearchParams();
      params.append('name', editingFile.name);
      params.append('description', editingFile.description || '');
      params.append('category', editingFile.category);

      await axios.put(`${API}/file-library/${editingFile.id}?${params.toString()}`);
      toast.success('File updated');
      setEditingFile(null);
      fetchFiles();
    } catch (error) {
      toast.error('Failed to update file');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await axios.delete(`${API}/file-library/${fileId}`);
      toast.success('File deleted');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const filteredFiles = files.filter(file => {
    const matchesSearch = 
      file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.original_filename.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || file.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#1C1C1C]">File Library</h1>
          <p className="text-[#66605B] mt-1">Manage documents for email attachments</p>
        </div>
        <Button 
          onClick={() => setUploadDialogOpen(true)}
          className="bg-[#1B4D3E] hover:bg-[#153E32]"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload File
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Files Grid */}
      {filteredFiles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFiles.map((file) => (
            <div
              key={file.id}
              className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-3">
                {getFileIcon(file.content_type, file.original_filename)}
                <div className="flex-1 min-w-0">
                  {editingFile?.id === file.id ? (
                    <div className="space-y-2">
                      <Input
                        value={editingFile.name}
                        onChange={(e) => setEditingFile(prev => ({ ...prev, name: e.target.value }))}
                        className="h-8"
                      />
                      <Select 
                        value={editingFile.category} 
                        onValueChange={(v) => setEditingFile(prev => ({ ...prev, category: v }))}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Textarea
                        value={editingFile.description || ''}
                        onChange={(e) => setEditingFile(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Description..."
                        rows={2}
                        className="text-sm"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleUpdateFile} className="bg-[#1B4D3E] hover:bg-[#153E32]">
                          <Save className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingFile(null)}>
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-medium text-[#1C1C1C] truncate" title={file.name}>
                        {file.name}
                      </h3>
                      <p className="text-xs text-[#66605B] truncate" title={file.original_filename}>
                        {file.original_filename}
                      </p>
                      <span className="inline-block px-2 py-0.5 bg-[#F5F3F0] text-[#66605B] text-xs rounded mt-1">
                        {categories.find(c => c.value === file.category)?.label || file.category}
                      </span>
                      {file.description && (
                        <p className="text-sm text-[#66605B] mt-2 line-clamp-2">{file.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">
                        {formatFileSize(file.file_size)}
                      </p>
                    </>
                  )}
                </div>
              </div>
              
              {editingFile?.id !== file.id && (
                <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${file.file_path}`} target="_blank" rel="noopener noreferrer">
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingFile(file)}
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(file.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-[#66605B]">
            {searchTerm || filterCategory !== 'all' 
              ? 'No files match your search'
              : 'No files in library yet'
            }
          </p>
          {!searchTerm && filterCategory === 'all' && (
            <Button 
              onClick={() => setUploadDialogOpen(true)}
              className="mt-4 bg-[#1B4D3E] hover:bg-[#153E32]"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Your First File
            </Button>
          )}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload File to Library</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>File *</Label>
              <Input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
              />
              {uploadForm.file && (
                <p className="text-sm text-[#66605B]">
                  Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Rental Agreement 2024"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={uploadForm.category} 
                onValueChange={(v) => setUploadForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this file..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpload}
                disabled={uploading || !uploadForm.file || !uploadForm.name.trim()}
                className="bg-[#1B4D3E] hover:bg-[#153E32]"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
